// src/hooks/useQueryParamSync.js
//
// Mirrors a search query INTO the URL (?q=…) so full-page list searches are
// shareable, bookmarkable, and restored on reload. Opt-in via `enabled` —
// embedded selectors / popovers leave it off and never touch the URL. Safe to
// call unconditionally: every <Search> instance renders under BrowserRouter.
//
// Write-only by design. The initial query is seeded FROM the URL by the caller
// (Search reads ?q= into its initialQuery on mount), so this hook only needs to
// push query -> URL. A bidirectional URL -> query effect is deliberately NOT
// used: it oscillated against this write when the seeded query differed from the
// URL on mount (e.g. the Purchase Order "MH" seed on an empty URL), tripping
// React's "Maximum update depth exceeded". See issue #482.

import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export default function useQueryParamSync(
  query,
  { enabled = false, key = "q" } = {},
) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Always compare against the freshest params (avoids a stale-closure write)
  // while keeping the effect keyed only on `query`.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  useEffect(() => {
    if (!enabled) return;
    const current = searchParamsRef.current.get(key) || "";
    const value = query || "";
    if (current === value) return; // already in sync — nothing to write
    const next = new URLSearchParams(searchParamsRef.current);
    if (value) next.set(key, value);
    else next.delete(key);
    // `replace` avoids spamming history on every debounced change.
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, enabled]);
}
